import { useEffect, useState } from 'react';

/**
 * Hash routes, so the static build needs no server-side routing. `navigate` pushes (or replaces) a hash and
 * tells every listener, including for replaceState, which fires no hashchange event.
 */
const EVENT = 'cyberscore:navigate';

export function navigate(hash: string, opts: { replace?: boolean } = {}) {
  if (opts.replace) {
    history.replaceState(history.state, '', hash);
    window.dispatchEvent(new Event(EVENT));
  } else window.location.hash = hash;
}

export function useHash(): string {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const on = () => setHash(window.location.hash);
    window.addEventListener('hashchange', on);
    window.addEventListener(EVENT, on);
    return () => {
      window.removeEventListener('hashchange', on);
      window.removeEventListener(EVENT, on);
    };
  }, []);
  return hash;
}

export type Route =
  | { name: 'assessments' }
  | { name: 'assessment'; id: string }
  | { name: 'answer'; id: string; question: string | null }
  | { name: 'chat'; id: string }
  | { name: 'documents'; id: string }
  | { name: 'score'; id: string }
  | { name: 'review'; id: string }
  /** Messages: every conversation, with one open (an assessment's thread) or none. */
  | { name: 'messages'; id: string | null }
  | { name: 'insurers'; id: string }
  | { name: 'insurer-forms' }
  | { name: 'notices' }
  | { name: 'client-fields' }
  | { name: 'submissions' }
  | { name: 'campaigns' }
  | { name: 'alerts' }
  | { name: 'integrations' }
  | { name: 'conflicts' }
  | { name: 'suppliers' }
  | { name: 'apply'; token: string }
  | { name: 'portfolio' }
  | { name: 'rescores' }
  | { name: 'evidence' }
  | { name: 'providers' }
  | { name: 'people' }
  | { name: 'roles' }
  | { name: 'clients' }
  | { name: 'client'; key: string }
  | { name: 'organisations' }
  | { name: 'branding' }
  | { name: 'tenant' }
  | { name: 'studio' }
  | { name: 'studio-journey'; id: string }
  | { name: 'studio-questions' }
  | { name: 'studio-question'; id: string }
  | { name: 'studio-rules' }
  | { name: 'studio-rule'; id: string }
  | { name: 'studio-profile'; id: string }
  | { name: 'studio-insurer-form'; id: string }
  | { name: 'releases' }
  | { name: 'chat-design' }
  | { name: 'home' }
  | { name: 'missions'; mission: string | null }
  | { name: 'connections' }
  | { name: 'locker' }
  | { name: 'insurance' }
  | { name: 'plan' }
  | { name: 'report' }
  | { name: 'badge'; token: string }
  | { name: 'passport' }
  | { name: 'pass'; token: string }
  | { name: 'sign-in' }
  | { name: 'sign-up' }
  | { name: 'verify'; token: string }
  | { name: 'forgot' }
  | { name: 'reset'; token: string }
  | { name: 'account' }
  | { name: 'unknown' };

const UUID = '[0-9a-f-]{36}';
const KEY = '[A-Za-z0-9][A-Za-z0-9_.-]{0,63}';

export function parseRoute(hash: string): Route {
  // A list's search, filters and page ride after a ? (task 60); the route is what comes before.
  const h = hash.replace(/^#/, '').split('?')[0]!;
  let m: RegExpMatchArray | null;
  if (h === '/assessments') return { name: 'assessments' };
  if (h === '/clients') return { name: 'clients' };
  // A client's own page (task 100): its key, as on the record.
  if ((m = h.match(new RegExp(`^/clients/(${KEY})$`))))
    return { name: 'client', key: decodeURIComponent(m[1]!) };
  if (h === '/portfolio') return { name: 'portfolio' };
  if (h === '/portfolio/rescores') return { name: 'rescores' };
  if (h === '/evidence') return { name: 'evidence' };
  if ((m = h.match(new RegExp(`^/assessments/(${UUID})$`)))) return { name: 'assessment', id: m[1]! };
  if ((m = h.match(new RegExp(`^/assessments/(${UUID})/answer(?:/(${KEY}))?$`))))
    return { name: 'answer', id: m[1]!, question: m[2] ?? null };
  if ((m = h.match(new RegExp(`^/assessments/(${UUID})/chat$`)))) return { name: 'chat', id: m[1]! };
  if ((m = h.match(new RegExp(`^/assessments/(${UUID})/score$`)))) return { name: 'score', id: m[1]! };
  if ((m = h.match(new RegExp(`^/assessments/(${UUID})/review$`)))) return { name: 'review', id: m[1]! };
  if ((m = h.match(new RegExp(`^/assessments/(${UUID})/messages$`)))) return { name: 'messages', id: m[1]! };
  if (h === '/messages') return { name: 'messages', id: null };
  if ((m = h.match(new RegExp(`^/assessments/(${UUID})/insurers$`)))) return { name: 'insurers', id: m[1]! };
  if (h === '/settings') return { name: 'tenant' };
  if (h === '/settings/insurer-forms') return { name: 'insurer-forms' };
  if (h === '/settings/notices') return { name: 'notices' };
  if (h === '/settings/client-fields') return { name: 'client-fields' };
  if (h === '/submissions') return { name: 'submissions' };
  if (h === '/campaigns') return { name: 'campaigns' };
  if (h === '/alerts') return { name: 'alerts' };
  if (h === '/settings/integrations') return { name: 'integrations' };
  if (h === '/conflicts') return { name: 'conflicts' };
  if (h === '/suppliers') return { name: 'suppliers' };
  // A submission link's public form (task 68): open to anyone, signed in or not.
  if ((m = h.match(/^\/apply\/([A-Za-z0-9_-]{22})$/))) return { name: 'apply', token: m[1]! };
  if ((m = h.match(new RegExp(`^/assessments/(${UUID})/documents$`))))
    return { name: 'documents', id: m[1]! };
  if (h === '/settings/ai-providers') return { name: 'providers' };
  if (h === '/settings/people') return { name: 'people' };
  if (h === '/settings/roles') return { name: 'roles' };
  if (h === '/settings/organisations') return { name: 'organisations' };
  if (h === '/settings/branding') return { name: 'branding' };
  if (h === '/studio') return { name: 'studio' };
  if (h === '/studio/releases') return { name: 'releases' };
  if ((m = h.match(new RegExp(`^/studio/journeys/(${UUID})$`)))) return { name: 'studio-journey', id: m[1]! };
  if (h === '/studio/questions') return { name: 'studio-questions' };
  if ((m = h.match(new RegExp(`^/studio/questions/(${UUID})$`))))
    return { name: 'studio-question', id: m[1]! };
  if (h === '/studio/rules') return { name: 'studio-rules' };
  if ((m = h.match(new RegExp(`^/studio/rules/(${UUID})$`)))) return { name: 'studio-rule', id: m[1]! };
  if ((m = h.match(new RegExp(`^/studio/profiles/(${UUID})$`)))) return { name: 'studio-profile', id: m[1]! };
  if ((m = h.match(new RegExp(`^/studio/insurer-forms/(${UUID})$`))))
    return { name: 'studio-insurer-form', id: m[1]! };
  if (h === '/design/chat') return { name: 'chat-design' };
  // The business owner's own pages, about the assessment they are working on.
  if (h === '/home') return { name: 'home' };
  if ((m = h.match(/^\/missions(?:\/([a-z_]{1,40}))?$/))) return { name: 'missions', mission: m[1] ?? null };
  if (h === '/connections') return { name: 'connections' };
  if (h === '/locker') return { name: 'locker' };
  if (h === '/insurance') return { name: 'insurance' };
  if (h === '/plan') return { name: 'plan' };
  if (h === '/report') return { name: 'report' };
  // A business's public score badge (decision 0032): open to anyone, signed in or not.
  if ((m = h.match(/^\/b\/([A-Za-z0-9_-]{22})$/))) return { name: 'badge', token: m[1]! };
  if (h === '/passport') return { name: 'passport' };
  // A cyber passport's public page (task 76): open to anyone with the link.
  if ((m = h.match(/^\/pass\/([A-Za-z0-9_-]{22})$/))) return { name: 'pass', token: m[1]! };
  // Signing in (decision 0033). Links from emails carry their token after the #, never to a server.
  if (h === '/sign-in') return { name: 'sign-in' };
  if (h === '/sign-up') return { name: 'sign-up' };
  if ((m = h.match(/^\/verify\/([A-Za-z0-9_-]{43})$/))) return { name: 'verify', token: m[1]! };
  if (h === '/forgot') return { name: 'forgot' };
  if ((m = h.match(/^\/reset\/([A-Za-z0-9_-]{43})$/))) return { name: 'reset', token: m[1]! };
  if (h === '/account') return { name: 'account' };
  return { name: 'unknown' };
}

export const href = {
  assessments: (query = '') => `#/assessments${query}`,
  clients: () => '#/clients',
  client: (key: string, tab?: string) => `#/clients/${encodeURIComponent(key)}${tab ? `?tab=${tab}` : ''}`,
  assessment: (id: string) => `#/assessments/${id}`,
  answer: (id: string, question?: string) => `#/assessments/${id}/answer${question ? `/${question}` : ''}`,
  chat: (id: string) => `#/assessments/${id}/chat`,
  score: (id: string) => `#/assessments/${id}/score`,
  review: (id: string) => `#/assessments/${id}/review`,
  /** A conversation, or (no id) the list of them. */
  messages: (id: string | null, question?: string) =>
    id
      ? `#/assessments/${id}/messages${question ? `?question=${encodeURIComponent(question)}` : ''}`
      : '#/messages',
  insurers: (id: string) => `#/assessments/${id}/insurers`,
  insurerForms: () => '#/settings/insurer-forms',
  notices: () => '#/settings/notices',
  clientFields: () => '#/settings/client-fields',
  integrations: () => '#/settings/integrations',
  submissions: (query = '') => `#/submissions${query}`,
  campaigns: () => '#/campaigns',
  alerts: (query = '') => `#/alerts${query}`,
  conflicts: (query = '') => `#/conflicts${query}`,
  suppliers: (query = '') => `#/suppliers${query}`,
  documents: (id: string) => `#/assessments/${id}/documents`,
  portfolio: () => '#/portfolio',
  rescores: () => '#/portfolio/rescores',
  evidence: () => '#/evidence',
  providers: () => '#/settings/ai-providers',
  people: () => '#/settings/people',
  roles: () => '#/settings/roles',
  organisations: () => '#/settings/organisations',
  branding: () => '#/settings/branding',
  /** Tenant 360 (task 102): the organisation's own record, its settings in tabs. */
  tenant: (tab?: string) => `#/settings${tab ? `?tab=${tab}` : ''}`,
  studio: () => '#/studio',
  journey: (id: string) => `#/studio/journeys/${id}`,
  releases: () => '#/studio/releases',
  questions: () => '#/studio/questions',
  question: (id: string) => `#/studio/questions/${id}`,
  rules: () => '#/studio/rules',
  rule: (id: string) => `#/studio/rules/${id}`,
  profile: (id: string) => `#/studio/profiles/${id}`,
  insurerForm: (id: string) => `#/studio/insurer-forms/${id}`,
  chatDesign: () => '#/design/chat',
  home: () => '#/home',
  missions: (id?: string) => `#/missions${id ? `/${id}` : ''}`,
  connections: () => '#/connections',
  locker: () => '#/locker',
  insurance: () => '#/insurance',
  plan: () => '#/plan',
  report: () => '#/report',
  badge: (token: string) => `#/b/${token}`,
  passport: () => '#/passport',
  pass: (token: string) => `#/pass/${token}`,
  signIn: () => '#/sign-in',
  signUp: () => '#/sign-up',
  forgot: () => '#/forgot',
  account: () => '#/account',
};

/** The query string of the current hash (#/clients?q=kinta → q=kinta). */
export const hashQuery = (hash: string) => new URLSearchParams(hash.split('?')[1] ?? '');
